import { useState } from 'react';

import { GlobalAccount, TaskView } from '@ndvr/customer-api';

import { MultiFilter, MultiFilterSection } from '@/components/MultiFilter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import { TimePeriodUnit, useTaskViewState } from '../task-state';
import { TaskAssigneeFilter } from './TaskAssigneeFilter';
import { TaskHouseholdFilter } from './TaskHouseholdFilter';
import { TaskStatusFilter } from './TaskStatusFilter';
import { TaskTypeFilter } from './TaskTypeFilter';

type FilterType = 'timePeriod' | 'assignee' | 'household' | 'taskType' | 'taskStatus';

interface TaskMultiFilterProps {
    tasks: TaskView[];
    householdMap: Map<string, GlobalAccount>;
}

const DEFAULT_TIME_PERIOD_VALUE = 30;
const DEFAULT_TIME_PERIOD_UNIT = TimePeriodUnit.DAY;

const TimePeriodFilterContent = ({
    initialValue,
    initialUnit,
}: {
    initialValue: number;
    initialUnit: TimePeriodUnit;
}) => {
    const { setTaskViewState } = useTaskViewState();
    const [tempValue, setTempValue] = useState(initialValue);
    const [tempUnit, setTempUnit] = useState<TimePeriodUnit>(initialUnit);

    const handleApply = () => {
        setTaskViewState({ timePeriodValue: tempValue || 1, timePeriodUnit: tempUnit });
    };

    return (
        <div className="space-y-4 p-4">
            <div className="flex items-center space-x-2">
                <Input
                    type="number"
                    min="1"
                    value={tempValue}
                    onChange={(e) => setTempValue(parseInt(e.target.value) || 1)}
                    className="w-20"
                />
                <Select value={tempUnit} onValueChange={(val) => setTempUnit(val as TimePeriodUnit)}>
                    <SelectTrigger className="w-[110px]">
                        <SelectValue placeholder="Unit" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectGroup>
                            <SelectItem value={TimePeriodUnit.DAY}>Days</SelectItem>
                            <SelectItem value={TimePeriodUnit.WEEK}>Weeks</SelectItem>
                            <SelectItem value={TimePeriodUnit.MONTH}>Months</SelectItem>
                            <SelectItem value={TimePeriodUnit.YEAR}>Years</SelectItem>
                        </SelectGroup>
                    </SelectContent>
                </Select>
            </div>
            <Button className="w-full" size="sm" onClick={handleApply}>
                Apply Filter
            </Button>
        </div>
    );
};

export const TaskMultiFilter = ({ tasks, householdMap }: TaskMultiFilterProps) => {
    const { taskViewState, setTaskViewState } = useTaskViewState();
    const [searchTerm, setSearchTerm] = useState('');
    const [focusSearchTrigger, setFocusSearchTrigger] = useState(0);
    const [dropdownOpenKey, setDropdownOpenKey] = useState(0);

    const isTimePeriodActive =
        taskViewState.timePeriodValue !== DEFAULT_TIME_PERIOD_VALUE ||
        taskViewState.timePeriodUnit !== DEFAULT_TIME_PERIOD_UNIT;

    const resetSearch = (open = true) => {
        if (open) {
            setSearchTerm('');
            setDropdownOpenKey((k) => k + 1);
            setTimeout(() => {
                setFocusSearchTrigger((prev) => prev + 1);
            }, 100);
        }
    };

    const sections: MultiFilterSection<FilterType>[] = [
        {
            id: 'assignee',
            label: 'Assignee',
            count: taskViewState.selectedAssignees.length,
            onClear: () => setTaskViewState({ selectedAssignees: [] }),
            content: (
                <TaskAssigneeFilter
                    tasks={tasks}
                    searchTerm={searchTerm}
                    onSearchChange={setSearchTerm}
                    focusSearchTrigger={focusSearchTrigger}
                />
            ),
        },
        {
            id: 'household',
            label: 'Household',
            count: taskViewState.selectedHouseholds.length,
            onClear: () => setTaskViewState({ selectedHouseholds: [] }),
            content: (
                <TaskHouseholdFilter
                    tasks={tasks}
                    householdMap={householdMap}
                    searchTerm={searchTerm}
                    onSearchChange={setSearchTerm}
                    focusSearchTrigger={focusSearchTrigger}
                />
            ),
        },
        {
            id: 'taskType',
            label: 'Task Type',
            count: taskViewState.selectedTaskTypes.length,
            onClear: () => setTaskViewState({ selectedTaskTypes: [] }),
            content: (
                <TaskTypeFilter
                    tasks={tasks}
                    searchTerm={searchTerm}
                    onSearchChange={setSearchTerm}
                    focusSearchTrigger={focusSearchTrigger}
                />
            ),
        },
        {
            id: 'taskStatus',
            label: 'Status',
            count: taskViewState.selectedTaskStatuses.length,
            onClear: () => setTaskViewState({ selectedTaskStatuses: [] }),
            content: (
                <TaskStatusFilter
                    tasks={tasks}
                    searchTerm={searchTerm}
                    onSearchChange={setSearchTerm}
                    focusSearchTrigger={focusSearchTrigger}
                />
            ),
        },
        {
            id: 'timePeriod',
            label: 'Time Period',
            count: isTimePeriodActive ? 1 : 0,
            onClear: () =>
                setTaskViewState({
                    timePeriodValue: DEFAULT_TIME_PERIOD_VALUE,
                    timePeriodUnit: DEFAULT_TIME_PERIOD_UNIT,
                }),
            content: (
                <TimePeriodFilterContent
                    key={`${dropdownOpenKey}-${taskViewState.timePeriodValue}-${taskViewState.timePeriodUnit}`}
                    initialValue={taskViewState.timePeriodValue}
                    initialUnit={taskViewState.timePeriodUnit}
                />
            ),
        },
    ];

    return (
        <div className="relative">
            <MultiFilter
                sections={sections}
                defaultSectionId="assignee"
                dropdownClassName="h-80 w-96 p-0"
                contentClassName="min-h-0 flex-1"
                onOpenChange={resetSearch}
                onSectionChange={() => resetSearch()}
            />
        </div>
    );
};
